export interface JurisdictionConfig {
  countryCode: string;
  countryName: string;
  flag: string;
  currency: string;
  currencySymbol: string;
  locale: string;
  taxIdLabel: string;
  taxIdPlaceholder: string;
  individualTaxIdLabel: string;
  accountingStandard: string;
  invoiceTaxLabel: string;
  defaultTaxRatePercent: number;
  statutoryWithholdingLines: string[];
  pensionSystemName: string;
  sampleOrgName: string;
  sampleAddress: string;
  filingRegimes: { key: string; label: string }[];
}

export const JURISDICTIONS: Record<string, JurisdictionConfig> = {
  US: {
    countryCode: 'US',
    countryName: 'United States',
    flag: '🇺🇸',
    currency: 'USD',
    currencySymbol: '$',
    locale: 'en-US',
    taxIdLabel: 'EIN (Employer ID No.)',
    taxIdPlaceholder: 'XX-XXXXXXX',
    individualTaxIdLabel: 'SSN / ITIN',
    accountingStandard: 'US GAAP',
    invoiceTaxLabel: 'Sales Tax',
    defaultTaxRatePercent: 8.5,
    statutoryWithholdingLines: [
      'Federal Income Tax (FIT)',
      'Social Security (FICA 6.2%)',
      'Medicare (FICA 1.45%)',
      'State Income Tax (SIT)',
      '401(k) Retirement Plan',
    ],
    pensionSystemName: '401(k) / Social Security',
    sampleOrgName: 'THREE THIRTY LLC',
    sampleAddress: '1112 Edenton Street, Birmingham, AL 35242, United States',
    filingRegimes: [
      { key: 'standard_w4', label: 'Form W-4 Standard (Single / Joint)' },
      { key: 'itemized', label: 'Itemized Deductions (Section 179)' },
    ],
  },
  IN: {
    countryCode: 'IN',
    countryName: 'India',
    flag: '🇮🇳',
    currency: 'INR',
    currencySymbol: '₹',
    locale: 'en-IN',
    taxIdLabel: 'GSTIN / PAN',
    taxIdPlaceholder: '29ABCDE1234F1Z5',
    individualTaxIdLabel: 'PAN / Aadhaar',
    accountingStandard: 'Indian Schedule III / Ind-AS',
    invoiceTaxLabel: 'GST (CGST/SGST/IGST)',
    defaultTaxRatePercent: 18.0,
    statutoryWithholdingLines: [
      'Provident Fund (EPF 12%)',
      'Employees State Insurance (ESIC)',
      'Professional Tax (PT)',
      'TDS (Section 192 / 115BAC)',
    ],
    pensionSystemName: 'Employees Provident Fund (EPF)',
    sampleOrgName: 'THREE THIRTY TECHNOLOGIES PVT LTD',
    sampleAddress: 'Prestige Tech Park, Marathahalli, Bengaluru, KA 560103, India',
    filingRegimes: [
      { key: 'new', label: 'New Tax Regime (Section 115BAC - Default Concessional)' },
      { key: 'old', label: 'Old Tax Regime (With 80C, 80D, HRA Exemptions)' },
    ],
  },
  GB: {
    countryCode: 'GB',
    countryName: 'United Kingdom',
    flag: '🇬🇧',
    currency: 'GBP',
    currencySymbol: '£',
    locale: 'en-GB',
    taxIdLabel: 'VAT Reg No / UTR',
    taxIdPlaceholder: 'GB123456789',
    individualTaxIdLabel: 'National Insurance (NINO)',
    accountingStandard: 'UK GAAP / FRS 102',
    invoiceTaxLabel: 'VAT (20%)',
    defaultTaxRatePercent: 20.0,
    statutoryWithholdingLines: [
      'PAYE Income Tax',
      'National Insurance (NIC Class 1)',
      'Workplace Auto-Enrolment Pension',
    ],
    pensionSystemName: 'Workplace Pension (5%+3%)',
    sampleOrgName: 'THREE THIRTY UK LIMITED',
    sampleAddress: '100 Bishopsgate, London EC2N 4AG, United Kingdom',
    filingRegimes: [
      { key: 'paye_standard', label: 'HMRC Standard PAYE (Tax Code 1257L)' },
      { key: 'scotland', label: 'Scottish Rate of Income Tax (SRIT)' },
    ],
  },
  AE: {
    countryCode: 'AE',
    countryName: 'United Arab Emirates',
    flag: '🇦🇪',
    currency: 'AED',
    currencySymbol: 'AED',
    locale: 'en-AE',
    taxIdLabel: 'TRN (Tax Registration No.)',
    taxIdPlaceholder: '100123456700003',
    individualTaxIdLabel: 'Emirates ID',
    accountingStandard: 'IFRS / UAE Federal Tax',
    invoiceTaxLabel: 'VAT (5%)',
    defaultTaxRatePercent: 5.0,
    statutoryWithholdingLines: [
      'Wage Protection System (WPS)',
      'End of Service Gratuity (EOSG)',
      '0% Personal Income Tax',
    ],
    pensionSystemName: 'End of Service Gratuity (EOSG)',
    sampleOrgName: 'THREE THIRTY MIDDLE EAST FZ-LLC',
    sampleAddress: 'Dubai Internet City, Building 4, Dubai, UAE',
    filingRegimes: [
      { key: 'wps_standard', label: 'MOHRE Wage Protection System (0% PIT)' },
    ],
  },
  SG: {
    countryCode: 'SG',
    countryName: 'Singapore',
    flag: '🇸🇬',
    currency: 'SGD',
    currencySymbol: 'S$',
    locale: 'en-SG',
    taxIdLabel: 'UEN (Unique Entity Number)',
    taxIdPlaceholder: '202012345M',
    individualTaxIdLabel: 'NRIC / FIN',
    accountingStandard: 'Singapore FRS',
    invoiceTaxLabel: 'GST (9%)',
    defaultTaxRatePercent: 9.0,
    statutoryWithholdingLines: [
      'Central Provident Fund (CPF)',
      'Skills Development Levy (SDL)',
    ],
    pensionSystemName: 'Central Provident Fund (CPF)',
    sampleOrgName: 'THREE THIRTY PTE LTD',
    sampleAddress: '1 Marina Boulevard, #28-00, Singapore 018989',
    filingRegimes: [
      { key: 'iras_resident', label: 'IRAS Singapore Resident (CPF Tier 1)' },
      { key: 'iras_non_resident', label: 'IRAS Non-Resident (Flat 15%/22%)' },
    ],
  },
  GLOBAL: {
    countryCode: 'GLOBAL',
    countryName: 'Global / International',
    flag: '🌐',
    currency: 'USD',
    currencySymbol: '$',
    locale: 'en-US',
    taxIdLabel: 'Tax ID / Registration',
    taxIdPlaceholder: 'TAX-REG-001',
    individualTaxIdLabel: 'National Tax ID',
    accountingStandard: 'IFRS Universal',
    invoiceTaxLabel: 'Tax (10%)',
    defaultTaxRatePercent: 10.0,
    statutoryWithholdingLines: [
      'Withholding Tax (WHT)',
      'Social Security / Pension',
    ],
    pensionSystemName: 'Statutory Pension / Social Security',
    sampleOrgName: 'THREE THIRTY GLOBAL CORP',
    sampleAddress: 'Global Operations Center',
    filingRegimes: [
      { key: 'standard_wht', label: 'Standard Cross-Border Withholding (WHT)' },
    ],
  },
};


export function getJurisdiction(countryCode?: string): JurisdictionConfig {
  const code = (countryCode || 'US').toUpperCase().trim();
  return JURISDICTIONS[code] || JURISDICTIONS.US!;
}

/**
 * Format currency value according to jurisdiction standards.
 */
export function formatMoney(amount: number | undefined | null, countryCode?: string, overrideCurrency?: string): string {
  const val = Number(amount) || 0;
  const config = getJurisdiction(countryCode);
  const curr = overrideCurrency || config.currency;

  try {
    return new Intl.NumberFormat(config.locale, {
      style: 'currency',
      currency: curr,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(val);
  } catch {
    return `${config.currencySymbol}${val.toLocaleString(config.locale)}`;
  }
}

/**
 * Format general numeric value according to locale.
 */
export function formatNumber(value: number | undefined | null, countryCode?: string): string {
  const val = Number(value) || 0;
  const config = getJurisdiction(countryCode);
  return val.toLocaleString(config.locale);
}
